import React, { useEffect, useState, useRef } from 'react';
import { Modal, Button, Spinner, Form, ListGroup } from 'react-bootstrap';
import api from '../api';
import { useAuth } from '../contexts/AuthContext';

export default function AdminCustomerServiceModal({ show, onHide }) {
  const [conversations, setConversations] = useState([]); // 用户列表
  const [selectedUser, setSelectedUser] = useState(null); // 当前会话用户
  const [messages, setMessages] = useState([]);           // 当前会话消息
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [manualUserId, setManualUserId] = useState(''); // 新增：手动输入用户ID
  const messagesEndRef = useRef(null);
  const { socket, user } = useAuth();
  const firstOpenRef = useRef(true); // 新增：标记是否首次打开

  // 拉取所有有消息的用户
  const fetchConversations = async () => {
    try {
      const res = await api.get('/api/messages/conversations');
      setConversations(prev => {
        // 合并：保留本地已有的（如手动添加的），并补充后端返回的
        const backend = res.data.data || [];
        const merged = [...prev];
        backend.forEach(u => {
          if (!merged.some(x => x._id === u._id)) {
            merged.push(u);
          }
        });
        // 保证唯一
        return merged.filter((u, idx, arr) => arr.findIndex(x => x._id === u._id) === idx);
      });
    } catch {}
  };

  // 发送消息
  const sendMessage = async () => {
    if (!input.trim() || !selectedUser) return;
    setSending(true);
    try {
      await api.post('/api/messages', { to: selectedUser._id, content: input.trim() });
      setInput('');
      // 不再立即 fetchMessages，等待 socket 推送
    } catch (e) {
      alert(e.response?.data?.message || '发送失败');
    } finally {
      setSending(false);
    }
  };

  // 主动发起会话：输入用户ID或用户名后查找并选中
  const handleManualSelectUser = async () => {
    const keyword = manualUserId.trim();
    if (!keyword) return;
    // 先查 conversations 里是否有该用户（支持 id 或 username 匹配）
    const exist = conversations.find(
      u => u._id === keyword || u.username === keyword
    );
    if (exist) {
      setSelectedUser(exist);
      setManualUserId('');
      return;
    }
    try {
      let userObj = null;
      // 优先按ID查找（24位hex）
      if (/^[a-fA-F0-9]{24}$/.test(keyword)) {
        const res = await api.get(`/api/users/${keyword}`);
        userObj = res.data.data || res.data.user;
      }
      // 如果不是ID格式或查不到，再按用户名查找
      if (!userObj || !userObj._id) {
        const res = await api.get(`/api/admin/users?search=${encodeURIComponent(keyword)}`);
        let list = res.data;
        if (Array.isArray(list)) {
          userObj = list.find(u => u.username === keyword);
        } else if (Array.isArray(res.data.data)) {
          userObj = res.data.data.find(u => u.username === keyword);
        } else if (res.data.user) {
          userObj = res.data.user;
        }
      }
      if (userObj && userObj._id) {
        // conversations 里没有时插入到最前面，并且选中
        setConversations(prev => {
          // 过滤掉所有相同 _id 的用户，保证唯一
          const filtered = prev.filter(u => u._id !== userObj._id);
          return [{ ...userObj, unread: 0 }, ...filtered];
        });
        setSelectedUser({ ...userObj, unread: 0 });
        setManualUserId('');
      } else {
        alert('未找到该用户');
      }
    } catch {
      alert('未找到该用户');
    }
  };

  // 新增：删除与某用户的全部聊天记录（管理员专用）
  const handleDeleteChatHistory = async (userId) => {
    if (!window.confirm('确定要删除该用户的全部聊天记录？此操作不可恢复，用户端也会被清空。')) return;
    try {
      await api.delete(`/api/messages/with/${userId}`);
      // 删除本地消息和会话
      setMessages([]);
      setConversations(prev => prev.filter(u => u._id !== userId));
      setSelectedUser(null);
      alert('聊天记录已删除');
    } catch (e) {
      alert(e.response?.data?.message || '删除聊天记录失败');
    }
  };

  // 实时监听 newMessage
  useEffect(() => {
    if (!socket || !selectedUser || !user) return;
    const handler = (msg) => {
      // 只要和当前会话相关（from/to 有一个等于 selectedUser._id，另一个等于 user._id）就追加
      if (
        (String(msg.from) === String(selectedUser._id) && String(msg.to) === String(user._id)) ||
        (String(msg.to) === String(selectedUser._id) && String(msg.from) === String(user._id))
      ) {
        setMessages(prev => [...prev, msg]);
      }
    };
    socket.on('newMessage', handler);
    return () => socket.off('newMessage', handler);
  }, [socket, selectedUser, user]);

  // 自动滚动到底部
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  // 打开弹窗时拉取会话用户
  useEffect(() => {
    if (show) {
      if (firstOpenRef.current) {
        fetchConversations();
        firstOpenRef.current = false;
      }
      setSelectedUser(null);
      setMessages([]);
    } else {
      // 弹窗关闭时重置
      firstOpenRef.current = true;
    }
  }, [show]);

  // 选中用户时拉取消息并批量标记已读
  useEffect(() => {
    if (!selectedUser) return;

    const fetchAndMarkRead = async () => {
      setLoading(true);
      try {
        // 拉取消息
        const res = await api.get(`/api/messages/with/${selectedUser._id}`);
        const msgs = res.data.data || [];
        setMessages(msgs);

        // 找出未读消息，批量标记为已读
        const unreadIds = msgs.filter(
          m => !m.isRead && String(m.to) === String(user._id)
        ).map(m => m._id);

        await Promise.all(
          unreadIds.map(id => api.post(`/api/messages/${id}/read`).catch(() => {}))
        );

        // 标记已读后刷新会话列表
        fetchConversations();
      } catch {}
      setLoading(false);
    };

    fetchAndMarkRead();
  }, [selectedUser]);

  return (
    <Modal show={show} onHide={onHide} size="lg" centered dialogClassName="glass-modal">
      <Modal.Header closeButton>
        <Modal.Title>客服会话管理</Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ display: 'flex', minHeight: 400, background: '#181c24' }}>
        {/* 左侧用户列表 */}
        <div style={{ width: 180, borderRight: '1px solid #333', paddingRight: 8 }}>
          <h6 style={{ color: '#0ff' }}>会话用户</h6>
          {/* 新增：手动输入用户ID/用户名 */}
          <div style={{ marginBottom: 8, display: 'flex', gap: 4 }}>
            <Form.Control
              size="sm"
              placeholder="用户ID/用户名"
              value={manualUserId}
              onChange={e => setManualUserId(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') handleManualSelectUser(); }}
              style={{ flex: 1 }}
            />
            <Button size="sm" variant="info" onClick={handleManualSelectUser}>发起</Button>
          </div>
          <ListGroup variant="flush">
            {conversations.length === 0 && <ListGroup.Item className="text-muted">暂无会话</ListGroup.Item>}
            {conversations
              .filter((user, idx, arr) => arr.findIndex(u => u._id === user._id) === idx)
              .map(user => (
                // 解决 button 嵌套 button 问题：去掉 ListGroup.Item 的 action 属性，改用自定义点击高亮
                <ListGroup.Item
                  key={user._id}
                  onClick={() => setSelectedUser(user)}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    cursor: 'pointer',
                    paddingRight: 0,
                    background: selectedUser && selectedUser._id === user._id ? '#222c' : undefined,
                    color: selectedUser && selectedUser._id === user._id ? '#0ff' : undefined,
                    border: selectedUser && selectedUser._id === user._id ? '1px solid #0ff' : undefined
                  }}
                >
                  <span>{user.nickname || user.username || user._id}</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    {user.unread > 0 && <span className="badge bg-danger">{user.unread}</span>}
                    <Button
                      size="sm"
                      variant="outline-danger"
                      style={{
                        padding: '0 8px',
                        minWidth: 32,
                        fontSize: 13,
                        marginLeft: 4,
                        zIndex: 2,
                        background: 'rgba(24,28,36,0.95)'
                      }}
                      onClick={e => {
                        e.stopPropagation();
                        setConversations(prev => prev.filter(u => u._id !== user._id));
                        if (selectedUser && selectedUser._id === user._id) setSelectedUser(null);
                      }}
                    >删除</Button>
                  </div>
                </ListGroup.Item>
              ))}
          </ListGroup>
        </div>
        {/* 右侧消息区 */}
        <div style={{ flex: 1, paddingLeft: 16, display: 'flex', flexDirection: 'column' }}>
          {selectedUser ? (
            <>
              <div style={{ flex: 1, overflowY: 'auto', maxHeight: 350, marginBottom: 8 }}>
                {/* 聊天栏头部：显示删除聊天记录按钮，仅管理员可见 */}
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 4 }}>
                  {user?.role === 'admin' && (
                    <Button
                      size="sm"
                      variant="danger"
                      onClick={() => handleDeleteChatHistory(selectedUser._id)}
                    >
                      删除聊天记录
                    </Button>
                  )}
                </div>
                {loading ? (
                  <div className="text-center"><Spinner animation="border" size="sm" /> 加载中…</div>
                ) : (
                  messages.length === 0 ? <div className="text-muted text-center">暂无消息</div> : (
                    messages.map(msg => (
                      <div key={msg._id} style={{
                        textAlign: msg.from === selectedUser._id ? 'left' : 'right',
                        margin: '8px 0'
                      }}>
                        <div style={{
                          display: 'inline-block',
                          background: msg.from === selectedUser._id ? '#222' : '#0ff',
                          color: msg.from === selectedUser._id ? '#fff' : '#222',
                          borderRadius: 8,
                          padding: '6px 12px',
                          maxWidth: 260,
                          wordBreak: 'break-all',
                        }}>
                          {msg.content}
                        </div>
                        <div style={{ fontSize: 10, color: '#888', marginTop: 2 }}>
                          {new Date(msg.createdAt).toLocaleString()}
                        </div>
                      </div>
                    ))
                  )
                )}
                <div ref={messagesEndRef} />
              </div>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <Form.Control
                  type="text"
                  placeholder="请输入消息..."
                  value={input}
                  onChange={e => setInput(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') sendMessage(); }}
                  disabled={sending}
                  style={{ width: '70%' }}
                />
                <Button variant="primary" onClick={sendMessage} disabled={sending || !input.trim()} style={{ marginLeft: 8 }}>
                  {sending ? <Spinner animation="border" size="sm" /> : '发送'}
                </Button>
              </div>
            </>
          ) : (
            <div className="text-muted text-center" style={{ margin: 'auto' }}>请选择左侧用户会话</div>
          )}
        </div>
      </Modal.Body>
    </Modal>
  );
}
